import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { UNLOCKED_ORG_STATUSES } from "@/app/org-access-policy";
import { useMyOrg, isAllowedWhileLocked } from "@/hooks/use-my-org";

export interface RequireApprovedOrgProps {
  /**
   * Statuses that unlock the wrapped Outlet. Defaults to
   * UNLOCKED_ORG_STATUSES (approved/active only — "fully approved").
   * Coaches + Staff (#1785) pass STAFF_SETUP_ALLOWED_STATUSES instead, so an
   * owner can set up their team WHILE still onboarding (pending/in_review),
   * not just once approved — rejected/suspended stay locked either way.
   */
  allowedStatuses?: ReadonlySet<string>;
}

/**
 * RequireApprovedOrg — Layer 2, the outlet-level org-status lock. Wraps the
 * dashboard layout INSIDE RequireAuth, so it gates an already-authenticated
 * operator on their org's approval status.
 *
 * Three states, fail-closed:
 *   - loading → render a neutral shell skeleton. NEVER the operational Outlet
 *     (no flash-of-full-app) and NEVER a premature redirect (status unknown).
 *   - locked (status NOT in `allowedStatuses`) on a disallowed path →
 *     Navigate /getting-started. `/getting-started` itself is always allowed
 *     (LOCKED_ALLOWED_PREFIXES) so the gate can't loop on its own destination.
 *   - unlocked (status IN `allowedStatuses`) → render the operational <Outlet/>.
 */
export function RequireApprovedOrg({
  allowedStatuses = UNLOCKED_ORG_STATUSES,
}: RequireApprovedOrgProps = {}) {
  const view = useMyOrg();
  const { pathname } = useLocation();

  // Status not yet known — show a skeleton, render neither the operational app
  // nor a redirect until we can decide safely.
  if (view.kind === "loading") {
    return (
      <Box
        aria-label="Loading organization"
        role="status"
        sx={{ display: "grid", minHeight: "100dvh", placeItems: "center" }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  const status = view.org?.status ?? "";
  const locked = !allowedStatuses.has(status);

  if (locked && !isAllowedWhileLocked(pathname)) {
    return <Navigate to="/getting-started" replace />;
  }

  return <Outlet />;
}
